import React, { useState } from "react";
import { Col, Row } from "react-bootstrap";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useGetProcessingSummaryQuery } from "./processingApi";
import useTranslator from "../../../hooks/useTranslator";
const ProcessingCharts = () => {
  const [pieprocessingFilter, setPieprocessingFilter] = useState("monthly");
  const [semiFilter, setSemiFilter] = useState("monthly");
  const [tab, setTab] = useState("processing");
  const { _t } = useTranslator();

  let filterMap = {
    Today: "hourly",
    Weekly: "weekly",
    Monthly: "monthly",
  };
  const { data: processingChart } = useGetProcessingSummaryQuery({
    tab,
    filter: pieprocessingFilter,
  });
  const { data: semiChart } = useGetProcessingSummaryQuery({
    tab,
    filter: semiFilter,
  });
  const dynamicDataProcessing =
    processingChart?.data &&
      processingChart.data.total_cases_entered_in_processing > 0
      ? [
        {
          name: _t("cases_in_processing", "Cases in processing"),
          y:
            (processingChart.data.total_cases_in_processing /
              processingChart.data.total_cases_entered_in_processing) *
            100,
          sliced: true,
          selected: true,
          color: "#46C389",
        },
        {
          name: _t("cases_processed", "Cases Processed"),
          y:
            (processingChart.data.total_cases_processed /
              processingChart.data.total_cases_entered_in_processing) *
            100,
          color: "#FFBC55",
        },
        {
          name: _t("cases_combined", "Cases Combined"),
          y:
            (processingChart.data.total_cases_combined /
              processingChart.data.total_cases_entered_in_processing) *
            100,
          color: "#54C9C9",
        },
        {
          name: _t("cases_rejected", "Cases Rejected"),
          y:
            (processingChart.data.total_cases_rejected /
              processingChart.data.total_cases_entered_in_processing) *
            100,
          color: "#FE6565",
        },
      ]
      : [{ name: _t("no_data", "No Data"), y: 100, color: "#E8E8E8" }]; // Placeholder when no data is available

  const dynamicSemiProcessing =
    semiChart?.data && semiChart.data.total_treys > 0
      ? [
        {
          name: _t("cheeks_treys", "Total Cheeks Treys"),
          y:
            (semiChart.data.total_cheeks_treys / semiChart.data.total_treys) *
            100,
          color: "#46C389",
        },
        {
          name: _t("strips_treys", "Total Strips Treys"),
          y:
            (semiChart.data.total_striped_treys /
              semiChart.data.total_treys) *
            100,
          color: "#EE7225",
        },
      ]
      : [{ name: _t("no_data", "No Data"), y: 100, color: "#E8E8E8" }]; // Placeholder when no data is available

  const options = {
    chart: {
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      type: "pie",
    },
    title: {
      text: "",
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>",
    },
    accessibility: {
      point: {
        valueSuffix: "%",
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: false,
        },
        showInLegend: true,
      },
    },
    legend: {
      layout: "vertical", // Ensures items appear line by line
      align: "right", // Align to the right
      verticalAlign: "bottom", // Center vertically
      itemMarginBottom: 5, // Adds spacing between items
      itemStyle: {
        color: "#8C97A7", // Change legend text color
        fontSize: "14px",
      },
    },
    series: [
      {
        name: "Data",
        colorByPoint: true,
        data: dynamicDataProcessing,
      },
    ],
  };
  const optionsDonut = {
    chart: {
      plotBackgroundColor: null,
      plotBorderWidth: 0,
      plotShadow: false,
    },
    title: {
      text: `${semiChart?.data.total_treys}<br>Total Treys<br>`,
      align: "center",
      verticalAlign: "middle",
      y: 48,
      style: {
        fontSize: "1.1em",
      },
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>",
    },
    accessibility: {
      point: {
        valueSuffix: "%",
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: false,
        },
        startAngle: -90,
        endAngle: 90,
        center: ["50%", "75%"],
        size: "110%",
        showInLegend: true,
      },
    },
    legend: {
      layout: "horizontal", // Ensures items appear line by line
      align: "center", // Align to the right
      verticalAlign: "bottom", // Center vertically
      itemMarginBottom: 5, // Adds spacing between items
      itemStyle: {
        color: "#8C97A7", // Change legend text color
        fontSize: "14px",
      },
    },
    series: [
      {
        type: "pie",
        name: "Treys Overview",
        innerSize: "50%",
        data: dynamicSemiProcessing,
      },
    ],
  };
  const handlePieRipeningFilter = (selectedFilter) => {
    setPieprocessingFilter(filterMap[selectedFilter]);
  };
  const handleSemiFilter = (selectedFilter) => {
    setSemiFilter(filterMap[selectedFilter]);
  };
  return (
    <>
      <Row className="mb-3">
        <Col lg={6}>
          <div className="cmn_card">
            <div className="overview_header d-flex gap-2 align-items-center">
              <h6 className="mb-0 flex-grow-1">{_t("processing_overview", "Processing Overview")}</h6>
              <ul className="chart_filter">
                {["Today", "Weekly", "Monthly"].map((item, index) => (
                  <li
                    key={index}
                    onClick={() => handlePieRipeningFilter(item)}
                    className={
                      filterMap[item] === pieprocessingFilter ? "active" : ""
                    }
                  >
                    {_t(item)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-2">
              <HighchartsReact highcharts={Highcharts} options={options} />
            </div>
          </div>
        </Col>
        <Col lg={6}>
          <div className="cmn_card">
            <div className="overview_header d-flex gap-2 align-items-center">
              <h6 className="mb-0 flex-grow-1">{_t("treys_overview", "Treys Overview")}</h6>
              <ul className="chart_filter">
                {["Today", "Weekly", "Monthly"].map((item, index) => (
                  <li
                    key={index}
                    onClick={() => handleSemiFilter(item)}
                    className={filterMap[item] === semiFilter ? "active" : ""}
                  >
                    {_t(item)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-2 position-relative">
              <HighchartsReact highcharts={Highcharts} options={optionsDonut} />
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default ProcessingCharts;
